import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthCallback } from './components/AuthCallback';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import NotificationsPage from './pages/NotificationsPage';
import ChatPage from './pages/ChatPage';

// Public Pages
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import HowItWorksPage from './pages/HowItWorksPage';
import FAQPage from './pages/FAQPage';
import SecurityPage from './pages/SecurityPage';
import ContactPage from './pages/ContactPage';

// Client Pages
import ClientHome from './pages/client/ClientHome';
import ProvidersListPage from './pages/client/ProvidersListPage';
import ProviderDetailPage from './pages/client/ProviderDetailPage';
import ClientRequestsPage from './pages/client/ClientRequestsPage';
import ClientPetsPage from './pages/client/ClientPetsPage';

// Provider Pages
import ProviderDashboard from './pages/provider/ProviderDashboard';
import ProviderProfilePage from './pages/provider/ProviderProfilePage';
import ProviderRequestsPage from './pages/provider/ProviderRequestsPage';
import SubscriptionPage from './pages/provider/SubscriptionPage';
import VerificationPage from './pages/provider/VerificationPage';
import ProviderAnalyticsPage from './pages/provider/ProviderAnalyticsPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AccountSettingsPage from './pages/AccountSettingsPage';
import NotFound from './pages/NotFound';

import './App.css';

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id DURING RENDER (not in useEffect)
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/termos" element={<TermsPage />} />
      <Route path="/sobre" element={<AboutPage />} />
      <Route path="/como-funciona" element={<HowItWorksPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/seguranÃ§a" element={<SecurityPage />} />
      <Route path="/contactos" element={<ContactPage />} />
      
      {/* Onboarding */}
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } 
      />

      {/* Account Settings */}
      <Route 
        path="/account/settings" 
        element={
          <ProtectedRoute>
            <AccountSettingsPage />
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Client Routes */}
      <Route 
        path="/client/home" 
        element={
          <ProtectedRoute requiredRole="cliente">
            <ClientHome />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/providers" 
        element={
          <ProtectedRoute requiredRole="cliente">
            <ProvidersListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/providers/:providerId" 
        element={
          <ProtectedRoute requiredRole="cliente">
            <ProviderDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/requests" 
        element={
          <ProtectedRoute requiredRole="cliente">
            <ClientRequestsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/pets" 
        element={
          <ProtectedRoute requiredRole="cliente">
            <ClientPetsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/chat/:requestId" 
        element={
          <ProtectedRoute requiredRole="cliente">
            <ChatPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/profile" 
        element={
          <ProtectedRoute requiredRole="cliente">
            <ClientHome />
          </ProtectedRoute>
        } 
      />

      {/* Provider Routes */}
      <Route 
        path="/provider/dashboard" 
        element={
          <ProtectedRoute requiredRole="prestador">
            <ProviderDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/profile" 
        element={
          <ProtectedRoute requiredRole="prestador">
            <ProviderProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/requests" 
        element={
          <ProtectedRoute requiredRole="prestador">
            <ProviderRequestsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/chat/:requestId" 
        element={
          <ProtectedRoute requiredRole="prestador">
            <ChatPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/subscription" 
        element={
          <ProtectedRoute requiredRole="prestador">
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/subscription/success" 
        element={
          <ProtectedRoute requiredRole="prestador">
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/verification" 
        element={
          <ProtectedRoute requiredRole="prestador">
            <VerificationPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/analytics" 
        element={
          <ProtectedRoute requiredRole="prestador">
            <ProviderAnalyticsPage />
          </ProtectedRoute>
        } 
      />

      {/* Common Routes */}
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <ClientHome />
          </ProtectedRoute>
        } 
      />

      {/* Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <AppRouter />
            <Toaster position="top-center" />
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    <Analytics />
    </ThemeProvider>
  );
}

export default App;
